import { styled } from "goober";
import { formatTinesUnits } from "../../lib/formatTinesUnits";
import { monthlyTinesUnitsLimit } from "../../lib/session";
import { HelpTooltip } from "../HelpTooltip";
import { Text } from "../Text";
import { UnitsChartCard } from "./tinesUnitsChartLayout";
import { TinesUnitsLineChart } from "./TinesUnitsLineChart";
import { buildTinesUnitsTrendChart } from "./tinesUnitsTrendRows";
import { USAGE_FILL } from "./tinesUnitsStatus";
import { useMonitoringWorkflows } from "./useMonitoringWorkflows";
import { PanelHeader, TotalRow } from "./workflowsLayout";

const Stats = styled("div")`
  display: flex;
  gap: 24px;

  @container units-charts (max-width: 500px) {
    gap: 12px;
  }
`;

const Stat = styled(PanelHeader)`
  min-width: 0;
`;

const StatLabel = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export function TinesUnitsTrend({
  selectedPeriodStart,
  onSelectMonth,
}: {
  selectedPeriodStart: number;
  onSelectMonth: (periodStart: number) => void;
}) {
  const { report } = useMonitoringWorkflows();
  const trend = report.unitsTrend;
  const limit = monthlyTinesUnitsLimit();
  if (!trend || limit === 0) return null;

  const { columns, currentTotal, projectedTotal } =
    buildTinesUnitsTrendChart(trend);

  const projectedFraction =
    projectedTotal == null ? null : projectedTotal / limit;
  const overLimit = projectedTotal != null && projectedTotal > limit;

  const projectedValue = (
    <TotalRow>
      <Text variant="display-md">
        {projectedTotal == null ? "—" : formatTinesUnits(projectedTotal)}
      </Text>
      {projectedTotal == null ? (
        <Text variant="text-small" color="var(--text-secondary)">
          too early to project
        </Text>
      ) : (
        <Text
          variant="text-small"
          color={overLimit ? USAGE_FILL.critical : "var(--text-secondary)"}
        >
          {overLimit
            ? `${formatTinesUnits(projectedTotal - limit)} over limit`
            : `${Math.round((projectedFraction ?? 0) * 100)}% of limit`}
        </Text>
      )}
    </TotalRow>
  );

  return (
    <UnitsChartCard
      aria-label="Tines units over time"
      data-single-month={columns.length > 1 ? undefined : ""}
    >
      <Stats>
        <Stat>
          <Text variant="text-small-md">This month so far</Text>
          <TotalRow>
            <Text variant="display-md">{formatTinesUnits(currentTotal)}</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              of {formatTinesUnits(limit)}
            </Text>
          </TotalRow>
        </Stat>
        <Stat>
          <StatLabel>
            <Text variant="text-small-md">Projected end of month</Text>
            <HelpTooltip
              content={
                trend.projectedTotal == null
                  ? "Your projected Tines units through end of month based on activity so far this month."
                  : "Your projected Tines units through end of month based on the last 14 days of activity."
              }
            />
          </StatLabel>
          {projectedValue}
        </Stat>
      </Stats>

      {/* One month draws as a flat line across the chart, which reads as a trend. */}
      {columns.length > 1 && (
        <TinesUnitsLineChart
          columns={columns}
          limit={limit}
          selectedPeriodStart={selectedPeriodStart}
          onSelectMonth={onSelectMonth}
        />
      )}
    </UnitsChartCard>
  );
}
